import { apiGet } from "../office/apiClient";
import {
  AnalyseResponse,
  BlockScore,
  GeneratedEmailResponse,
  HistoryPage,
  RunDetail,
  Verdict,
} from "../types";

// Fetch the current user's recent analysis history (newest first).
export function getHistory(limit = 10): Promise<HistoryPage> {
  return apiGet<HistoryPage>(`/api/history/?limit=${limit}`);
}

// Fetch one stored run in full so its result screen can be re-opened.
export function getHistoryDetail(id: string): Promise<RunDetail> {
  return apiGet<RunDetail>(`/api/history/${id}`);
}

const FALLBACK_BLOCK: BlockScore = {
  key: "trustedPartner",
  score: 0,
  weight: 0,
  summary: "",
};

// Split a stored plaintext body back into paragraphs for the rewrite view. The
// "AI ADDED" highlighting is re-derived by DiffView from the original draft, so
// the paragraphs only need to carry the text.
function toParagraphs(body: string): AnalyseResponse["rewrite"]["paragraphs"] {
  const parts = body.split(/\n\s*\n/).map((t) => t.trim()).filter(Boolean);
  const src = parts.length > 0 ? parts : [body];
  return src.map((text) => ({ text, kind: "original" as const }));
}

// Rebuild an AnalyseResponse from a stored analyse run so ResultsView can render
// it. coachingDetails are persisted with the run (pre-generated at analyse time),
// so opening a note from history is instant — ResultsView only re-fetches a note
// the backend didn't pre-generate. insufficientContext isn't stored.
export function runToAnalyseResponse(run: RunDetail): AnalyseResponse {
  const score = run.originalScore ?? 0;
  return {
    overallScore: score,
    overallVerdict: (run.verdict ?? "needs_improvement") as Verdict,
    issuesFound: run.issuesFound ?? (run.coachingNotes?.length ?? 0),
    trustedPartner: run.trustedPartner ?? FALLBACK_BLOCK,
    selectedBlock: run.selectedBlock ?? "engage",
    blocks: run.blocks ?? [],
    coachingNotes: run.coachingNotes ?? [],
    coachingDetails: run.coachingDetails ?? [],
    rewrite: {
      paragraphs: toParagraphs(run.rewriteBody ?? ""),
      caption: "ASLAN-aligned rewrite of your original draft.",
      scoreBefore: run.scoreBefore ?? score,
      scoreAfter: run.scoreAfter ?? score,
      suggestedSubject: run.suggestedSubject ?? null,
      insufficientContext: false,
    },
    suggestedSubject: run.suggestedSubject ?? null,
    insufficientContext: false,
  };
}

// Rebuild a GeneratedEmailResponse from a stored generate run.
export function runToGeneratedEmail(run: RunDetail): GeneratedEmailResponse {
  return {
    body: run.generatedBody ?? run.originalBody ?? "",
    score: run.originalScore ?? 0,
    verdict: (run.verdict ?? "good") as Verdict,
    caption: "Generated using AI methodology",
    suggestedSubject: run.suggestedSubject ?? null,
  };
}
